import React from 'react';

import { TrackableList, TrackableObjectType } from 'types/trackables';

import { useTrackableLists } from 'trackables/hooks';
import { TrackableListSelect } from 'trackables/components/TrackableListSelect/TrackableListSelect';

import {
  formatCommaSeparatedValues,
  isValidUUID,
  parseCommaSeparatedValues,
} from './utils';
import { ReteInputProps } from 'pipelines/services/rete/controls/input';

type TrackableListSelectValue = readonly TrackableList[] | TrackableList | null;

function createPlaceholderList(id: string): TrackableList {
  return {
    id,
    name: id,
    description: null,
    object_type: 'custom',
    application_id: '',
    created_at: '',
    updated_at: '',
  };
}

export type TrackableListPropertyInputProps = ReteInputProps & {
  trackableListObjectTypes?: TrackableObjectType | TrackableObjectType[];
};

export function TrackableListPropertyInput({
  value,
  onChange,
  placeholder,
  trackableListObjectTypes,
}: TrackableListPropertyInputProps) {
  const parsedIds = React.useMemo(
    () => parseCommaSeparatedValues(value),
    [value]
  );

  const selectedIds = React.useMemo(
    () => parsedIds.filter(isValidUUID),
    [parsedIds]
  );

  React.useEffect(() => {
    if (!parsedIds.length) {
      return;
    }

    if (parsedIds.length !== selectedIds.length) {
      onChange?.(formatCommaSeparatedValues(selectedIds));
    }
  }, [parsedIds, selectedIds, onChange]);

  const selectedListsQuery = useTrackableLists({
    queryKey: ['trackable-lists-property', selectedIds],
    params: selectedIds.length
      ? {
          trackable_list_ids: selectedIds,
          limit: Math.max(selectedIds.length, 50),
        }
      : undefined,
    enabled: selectedIds.length > 0,
    keepPreviousData: true,
  });

  const selectedListsLookup = React.useMemo(() => {
    if (!selectedListsQuery.data?.data?.length) {
      return new Map<string, TrackableList>();
    }

    return new Map(
      selectedListsQuery.data.data.map((list) => [list.id, list] as const)
    );
  }, [selectedListsQuery.data]);

  const selectedLists = React.useMemo(() => {
    if (!selectedIds.length) {
      return [];
    }

    return selectedIds.map(
      (id) => selectedListsLookup.get(id) ?? createPlaceholderList(id)
    );
  }, [selectedIds, selectedListsLookup]);

  const normalizedObjectTypes = React.useMemo<
    TrackableObjectType[] | undefined
  >(() => {
    if (!trackableListObjectTypes) {
      return undefined;
    }

    const entries = Array.isArray(trackableListObjectTypes)
      ? trackableListObjectTypes
      : [trackableListObjectTypes];

    const cleaned = entries
      .map((entry) => entry?.trim())
      .filter(
        (entry): entry is TrackableObjectType =>
          entry === 'face' || entry === 'vehicle' || entry === 'custom'
      );

    if (!cleaned.length) {
      return undefined;
    }

    return Array.from(new Set(cleaned));
  }, [trackableListObjectTypes]);

  const handleSelectionChange = React.useCallback(
    (lists: TrackableListSelectValue) => {
      let selections: TrackableList[] = [];

      if (Array.isArray(lists)) {
        selections = lists as TrackableList[];
      } else if (lists) {
        selections = [lists as TrackableList];
      }

      const ids = selections
        .map((list) => list.id?.trim())
        .filter((id): id is string => Boolean(id));
      onChange?.(formatCommaSeparatedValues(ids));
    },
    [onChange]
  );

  let resolvedPlaceholder = 'Select trackables lists';
  if (typeof placeholder === 'string' && placeholder.length > 0) {
    resolvedPlaceholder = placeholder;
  }

  return (
    <TrackableListSelect<true>
      isMulti
      isClearable
      queryFilters={
        normalizedObjectTypes
          ? {
              object_types: normalizedObjectTypes,
            }
          : undefined
      }
      value={selectedLists}
      onChange={handleSelectionChange}
      placeholder={resolvedPlaceholder}
      isLoading={selectedListsQuery.isFetching && !selectedListsQuery.data}
      styles={{
        menuPortal: (provided) => ({
          ...provided,
          zIndex: 9999,
        }),
      }}
    />
  );
}
